import moment from "moment";
import { isEmpty } from "lodash";

export const INSIGHTS_LAUNCH_DATE = "2026-08-05";
const RXR_TRIAL_PERIOD_END = moment("2026-09-08");
export const ENERGY_PLUS_TRIAL_PERIOD_END = moment("2027-04-01");

/** Insights tabs that share the EnergyPlus free trial (through April 2027). */
export const ENERGY_PLUS_TRIAL_TABS = ["/energy-plus", "/utility-analysis", "/hybrid-heating", "/single-zone-cooling"];

const CURRENT_DATE = moment().format("YYYY-MM-DD");

export const hasActiveEnergyPlusTrial = () => moment(CURRENT_DATE).isBefore(ENERGY_PLUS_TRIAL_PERIOD_END);

/** Checks if the model has Insights activated */
export const hasInsightsAccess = ({ isInsightsActive = false } = {}) => {
    return isInsightsActive;
};

/** Checks if the model has access to room-by-room results */
export const hasRxRResultsAccess = ({ isRxRResultsUnlocked = false } = {}) => {
    return isRxRResultsUnlocked || hasActiveRxRTrial();
};

/** Checks if the model has room-by-room results generated before the launch date */
export const hasRxRResultsBeforeLaunch = ({ completedAt, roomByRoomResults = {} } = {}) => {
    if (!completedAt) return false;
    if (isEmpty(roomByRoomResults)) return false;

    return moment(completedAt).isBefore(INSIGHTS_LAUNCH_DATE);
};

/** Checks if the room-by-room 30-day trial period is active */
export const hasActiveRxRTrial = () => {
    return moment(CURRENT_DATE).isBefore(RXR_TRIAL_PERIOD_END);
};

/**
 * EnergyPlus, Utility Analysis, and Hybrid Heating stay free until April 2027
 * Room-by-room to stay free for 30 days after launch:
 * - Files with room-by-room results that were generated before launch day should have room-by-room accessible indefinitely
 * - Files created between launch day and launch day + 30 days will have room-by-room accessible for only that 30-day period
 */
export const hasAccessToInsightsTab = (tabUrl, { isRxRResultsUnlocked = false } = {}) => {
    if (ENERGY_PLUS_TRIAL_TABS.includes(tabUrl)) {
        return hasActiveEnergyPlusTrial();
    }
    else if (tabUrl === "/room-by-room") {
        return hasRxRResultsAccess({ isRxRResultsUnlocked });
    }
    else {
        return false;
    }
};

/** Calculates the number of days left in the room by room trial period */
const getTrialDaysLeft = () => {
    const daysLeft = RXR_TRIAL_PERIOD_END.diff(moment(CURRENT_DATE), "days");

    return `${daysLeft} ${daysLeft === 1 ? "day" : "days"}`;
};

/** Gets the info tooltip text for when the trial period ends */
export const getTrialText = (tabUrl, { isRxRResultsUnlocked = false } = {}) => {
    if (ENERGY_PLUS_TRIAL_TABS.includes(tabUrl)) {
        return "Free until April 2027. After that, activate Insights (1 credit) for permanent access.";
    }
    else if (tabUrl === "/room-by-room" && !isRxRResultsUnlocked) {
        const daysLeft = getTrialDaysLeft();
        return `Free trial ends in ${daysLeft}. Activate Insights (1 credit) for permanent access.`;
    }
    else {
        return "";
    }
};
